import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Chapter, ChapterShareState, ChapterShareMode } from '@simultrain/shared';
import { api } from '../api/client.js';

type Props = {
  chapter: Chapter;
  onClose: () => void;
};

export function ShareChapterModal({ chapter, onClose }: Props) {
  const { t } = useTranslation();
  const [state, setState] = useState<ChapterShareState | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api.getChapterShare(chapter.id).then(setState).catch((err) => setError(String(err))).finally(() => setLoading(false));
  }, [chapter.id]);

  async function enable(mode: ChapterShareMode) {
    setBusy(true);
    setError(null);
    try {
      setState(await api.createChapterShare(chapter.id, mode));
    } catch (err) {
      setError(String(err));
    } finally {
      setBusy(false);
    }
  }

  async function changeMode(mode: ChapterShareMode) {
    setBusy(true);
    setError(null);
    try {
      setState(await api.patchChapterShareMode(chapter.id, mode));
    } catch (err) {
      setError(String(err));
    } finally {
      setBusy(false);
    }
  }

  async function regenerate() {
    if (!window.confirm(t('chapters.shareRegenerateConfirm'))) return;
    setBusy(true);
    setError(null);
    try {
      setState(await api.rotateChapterShare(chapter.id));
    } catch (err) {
      setError(String(err));
    } finally {
      setBusy(false);
    }
  }

  async function disable() {
    if (!window.confirm(t('chapters.shareDisableConfirm'))) return;
    setBusy(true);
    setError(null);
    try {
      await api.deleteChapterShare(chapter.id);
      setState({ shareMode: 'private', shareToken: null, shareUrl: null });
    } catch (err) {
      setError(String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: 24,
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: 10, padding: 24,
        width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto',
      }}>
        <div className="row" style={{ justifyContent: 'space-between', marginBottom: 16 }}>
          <h2 style={{ margin: 0 }}>{t('chapters.shareTitle')}</h2>
          <button className="secondary" onClick={onClose}>✕</button>
        </div>

        {error && <div className="error-banner">{error}</div>}

        {loading || !state ? null : state.shareMode === 'private' ? (
          <>
            <p className="muted" style={{ fontSize: 13 }}>{t('chapters.shareIntro')}</p>
            <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
              <button disabled={busy} onClick={() => void enable('public')}>{t('chapters.sharePublic')}</button>
              <button disabled={busy} onClick={() => void enable('authenticated')}>{t('chapters.shareAuthenticated')}</button>
            </div>
          </>
        ) : (
          <>
            <div style={{ marginBottom: 12 }}>
              <label>{t('chapters.shareModeLabel')}</label>
              <select
                value={state.shareMode}
                disabled={busy}
                onChange={(e) => void changeMode(e.target.value as ChapterShareMode)}
              >
                <option value="public">{t('chapters.sharePublic')}</option>
                <option value="authenticated">{t('chapters.shareAuthenticated')}</option>
              </select>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label>{t('chapters.shareLinkLabel')}</label>
              <div className="row">
                <input type="text" value={state.shareUrl ?? ''} readOnly style={{ flex: 1 }} />
                <button type="button" onClick={() => void navigator.clipboard.writeText(state.shareUrl ?? '')}>
                  {t('enrollments.copy')}
                </button>
              </div>
            </div>
            <div className="row" style={{ gap: 8 }}>
              <button className="secondary" disabled={busy} onClick={() => void regenerate()}>
                {t('chapters.shareRegenerate')}
              </button>
              <button className="danger" disabled={busy} onClick={() => void disable()}>
                {t('chapters.shareDisable')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
